import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { UploadCloud, X, FileText, ExternalLink, Link2 } from "lucide-react";
import { api } from "@/lib/api";
import { WORKS_PERMIT_DOC_KINDS } from "@costadorada/shared";

export interface TypedAttachment {
  kind: string;
  title: string;
  fileUrl: string;
}

const isImage = (url: string) => /^data:image\//.test(url) || /\.(png|jpe?g|webp|gif|heic)(\?|$)/i.test(url);

/**
 * Supporting documents for a works permit: several files, each tagged with the kind
 * of document it is (plans, quote, contractor licence…). Pick the kind, then upload
 * one or more files; the kind and the title stay editable on every row.
 */
export function TypedAttachmentField({
  label,
  folder,
  value,
  onChange,
  accept = ".pdf,.doc,.docx,.jpg,.jpeg,.png,.heic,.webp",
  kinds = WORKS_PERMIT_DOC_KINDS,
  kindLabel: kindLabelProp,
  defaultKind,
  allowLink = false,
}: {
  label: string;
  folder: string;
  value: TypedAttachment[];
  onChange: (next: TypedAttachment[]) => void;
  accept?: string;
  /** Document kinds offered; defaults to the works-permit kinds. */
  kinds?: readonly string[];
  kindLabel?: (kind: string) => string;
  defaultKind?: string;
  /** Also let the user paste a link (Fireflies, Drive, a recording) instead of uploading. */
  allowLink?: boolean;
}) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [kind, setKind] = useState<string>(defaultKind ?? kinds[0]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [link, setLink] = useState("");
  const kindLabel = (k: string) => (kindLabelProp ? kindLabelProp(k) : t(`worksPermits.docKinds.${k}`));

  async function handleFiles(files: FileList) {
    setError(null);
    setUploading(true);
    const added: TypedAttachment[] = [];
    try {
      for (const file of Array.from(files)) {
        const form = new FormData();
        form.append("file", file);
        const { data } = await api.post(`/upload?folder=${folder}`, form, { headers: { "Content-Type": "multipart/form-data" } });
        added.push({ kind, title: file.name.replace(/\.[^.]+$/, ""), fileUrl: data.url });
      }
    } catch (err: any) {
      setError(err?.response?.data?.message ?? t("common.error"));
    } finally {
      // Keep whatever did upload before a failure.
      if (added.length) onChange([...value, ...added]);
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  const update = (i: number, patch: Partial<TypedAttachment>) => onChange(value.map((a, j) => (j === i ? { ...a, ...patch } : a)));

  return (
    <div>
      <label className="label">{label}</label>
      {value.length > 0 && (
        <ul className="mb-2 space-y-1.5">
          {value.map((a, i) => (
            <li key={`${a.fileUrl}-${i}`} className="grid grid-cols-1 md:grid-cols-[11rem_1fr_auto_auto] items-center gap-2 rounded border border-slate-200 bg-slate-50 px-2 py-1.5 text-sm">
              <select className="input text-xs py-1" value={a.kind} onChange={(e) => update(i, { kind: e.target.value })} aria-label={t("worksPermits.docKind")}>
                {kinds.map((k) => (
                  <option key={k} value={k}>{kindLabel(k)}</option>
                ))}
              </select>
              <div className="flex items-center gap-2 min-w-0">
                {isImage(a.fileUrl) ? (
                  <img src={a.fileUrl} alt="" className="h-8 w-8 rounded object-cover border border-slate-200 shrink-0" />
                ) : (
                  <FileText size={16} className="shrink-0 text-primary" />
                )}
                <input className="input text-xs py-1 flex-1 min-w-0" value={a.title} onChange={(e) => update(i, { title: e.target.value })} aria-label={t("worksPermits.docTitle")} required />
              </div>
              <a href={a.fileUrl} target="_blank" rel="noreferrer" className="text-primary hover:underline inline-flex items-center gap-1 text-xs" title={t("legal.open")}>
                <ExternalLink size={13} />
              </a>
              <button type="button" className="text-slate-400 hover:text-red-600" onClick={() => onChange(value.filter((_, j) => j !== i))} aria-label={t("common.delete")}>
                <X size={15} />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <select className="input max-w-xs text-sm" value={kind} onChange={(e) => setKind(e.target.value)} aria-label={t("worksPermits.docKind")}>
          {kinds.map((k) => (
            <option key={k} value={k}>{kindLabel(k)}</option>
          ))}
        </select>
        <input ref={inputRef} type="file" multiple accept={accept} className="hidden" onChange={(e) => { if (e.target.files?.length) handleFiles(e.target.files); }} />
        <button type="button" className="btn-secondary text-sm" disabled={uploading} onClick={() => inputRef.current?.click()}>
          {uploading ? t("legal.uploading") : (<><UploadCloud size={14} /> {t("worksPermits.addAttachment")}</>)}
        </button>
      </div>
      {allowLink && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <input
            type="url"
            className="input flex-1 min-w-[16rem] text-sm"
            placeholder={t("meetings.attachmentLink")}
            value={link}
            onChange={(e) => setLink(e.target.value)}
          />
          <button
            type="button"
            className="btn-secondary text-sm"
            disabled={!link.trim()}
            onClick={() => {
              const url = link.trim();
              if (!url) return;
              onChange([...value, { kind, title: kindLabel(kind), fileUrl: url }]);
              setLink("");
            }}
          >
            <Link2 size={14} /> {t("meetings.addLink")}
          </button>
        </div>
      )}
      <p className="mt-1 text-xs text-slate-500">{t("worksPermits.attachMultipleHint")}</p>
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}
